<template>
  <ProductDetailGallery
    :items="galleryItems"
    :lightbox-gallery="lightboxGallery"
    :is-loading="store.loading"
  />
</template>
<script setup lang="ts">
import { ProductDetailGallery } from '@hypercodestudio/basler-components';
import type { GalleryItem } from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailGallery.vue';
import { getProductImage } from '~/utils/shop/getProductImage';
import type { IMedia } from '~/utils/guards/isMediaType';
import type { MediaItemInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/Media.vue';
import { useMedia } from '~/composables/useMedia';
import { isDefined } from '~/utils/guards/isDefined';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import type { IProductEnrichment } from '~/lib/ContentfulService';
const store = useProductDetailPageStore();
const { $textDictionary, $globalPageSettings } = useNuxtApp();

const fallbackImage = $globalPageSettings.value?.fallbackPosterImage;

interface Props {
  productEnrichments: IProductEnrichment[];
  showExperimentalFunction?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productEnrichments: () => [],
  showExperimentalFunction: false
});

const productImagesThumbnails = computed<GalleryItem[]>(() => {
  if (!store.decoratedProduct) {
    return [];
  }

  return getProductImage(
    store.decoratedProduct.product,
    {
      sizes: '200px',
      widths: [400, 600, 800, 1000],
      aspectRatio: [4, 3]
    },
    $globalPageSettings.value?.defaultProductImage ?? undefined
  );
});

const productImages = computed<GalleryItem[]>(() => {
  if (!store.decoratedProduct) {
    return [];
  }

  return getProductImage(
    store.decoratedProduct.product,
    {
      sizes: '2400px',
      widths: [800, 1200, 1600, 2400],
      aspectRatio: [4, 3]
    },
    $globalPageSettings.value?.defaultProductImage ?? undefined
  );
});

const productEnrichmentGallery = computed<IMedia[]>(() => {
  const items: IMedia[] = [];
  props.productEnrichments.forEach((element) => {
    if (element?.fields?.contentSlotGallery) {
      element.fields.contentSlotGallery.forEach((item) => {
        items.push(item);
      });
    }
  });
  return items;
});

const productEnrichmentGalleryThumbnails = computed<GalleryItem[]>(() => {
  const items: GalleryItem[] = [];
  const imageAttributes = '?fm=webp&f=center&w=600&h=450&q=80&fit=pad';

  props.productEnrichments.forEach((element) => {
    if (element?.fields?.contentSlotGallery) {
      element.fields.contentSlotGallery.forEach((item) => {
        items.push({
          media: {
            type: 'image',
            content: {
              height: 450,
              width: 600,
              src: item.fields?.posterImage?.fields?.file?.url
                ? item.fields?.posterImage?.fields?.file?.url + imageAttributes
                : fallbackImage?.url + imageAttributes
            }
          }
        });
      });
    }
  });
  return items;
});

const media: Record<string, MediaItemInterface> = Object.assign(
  {},
  ...(
    await Promise.all(
      productEnrichmentGallery.value.map(async (item) => {
        const mediaItemInterface = await useMedia(item);
        if (!mediaItemInterface) {
          return undefined;
        }

        return {
          [item.sys.id]: mediaItemInterface satisfies MediaItemInterface
        };
      })
    )
  ).filter(isDefined)
);

const galleryItems = computed<GalleryItem[]>(() => {
  const items: GalleryItem[] = [];
  productImagesThumbnails.value.forEach((element) => {
    items.push(element);
  });
  productEnrichmentGalleryThumbnails.value.forEach((element) => {
    items.push(element);
  });
  return items;
});

const lightboxItems = computed<GalleryItem[]>(() => {
  const items: GalleryItem[] = [];
  productImages.value.forEach((element) => {
    items.push(element);
  });
  productEnrichmentGallery.value.forEach((element) => {
    items.push({ media: media[element.sys.id] });
  });
  return items;
});

const lightboxGallery = computed(() => {
  if (!store.decoratedProduct) {
    return [];
  }

  return {
    title:
      $textDictionary.value['media.gallery.title'] ??
      'Gallery of all media elements',
    isOpen: false,
    triggerBtnLabel:
      $textDictionary.value['media.gallery.trigger.btn.label'] ??
      'Open media in lightbox',
    nextBtnLabel:
      $textDictionary.value['media.gallery.next.btn.label'] ?? 'Next media',
    prevBtnLabel:
      $textDictionary.value['media.gallery.prev.btn.label'] ?? 'Previous media',
    closeBtnLabel:
      $textDictionary.value['media.gallery.close.btn.label'] ??
      'Close lightbox',
    mediaList: lightboxItems.value
  };
});
</script>
